/**
 * Ancient number systems: Babylonian, Egyptian hieroglyphic, Maya, Attic and Ionic Greek.
 *
 * What each system does:
 *
 * - Babylonian: base 60, positional. A digit 1–59 is written with 𒁹 (U+12079, one) and 𒌋
 *   (U+1230B, ten). Early scribes left a gap for an empty place, which makes 1 03 and 1 0 3
 *   ambiguous; from the Seleucid period (about 300 BCE) a placeholder sign marked a gap
 *   inside a number; mathematical texts do not write it at the end, so 1 and 60 look the same.
 * - Egyptian: base 10, additive, no zero. Seven signs for 1, 10, 100 … 10^6, each repeated up
 *   to nine times: the stroke (Z1), heel bone (V20), coil of rope (V1), lotus (M12), finger
 *   (D50), tadpole (I8) and the god Heh (C11).
 * - Maya: base 20, positional, with a shell for zero, written top to bottom. In the Long
 *   Count calendar the third place counts 18 twenties, not 20, so the place values run
 *   1, 20, 360, 7200, 144000: a tun is 360 days.
 * - Attic (acrophonic) Greek: base 10, additive, from the initial letters of the number
 *   words: Ι 1, Π (pente) 5, Δ (deka) 10, Η (hekaton) 100, Χ (khilioi) 1000, Μ (myrioi)
 *   10 000, with Π combined with Δ, Η, Χ and Μ for 50, 500, 5000 and 50 000.
 * - Ionic (Milesian, alphabetic) Greek: the 24 letters plus stigma, koppa and sampi stand
 *   for 1–9, 10–90 and 100–900; a keraia ʹ ends the numeral and a lower keraia ͵ marks
 *   thousands.
 */

const MAX_SAFE = Number.MAX_SAFE_INTEGER;

export const BABYLONIAN_MAX = 999_999_999_999;
export const EGYPTIAN_MAX = 9_999_999;
export const MAYA_MAX = 999_999_999;
export const ATTIC_MAX = 99_999;
export const IONIC_MAX = 9999;

function isCount(n: number, max: number): boolean {
  return Number.isInteger(n) && n >= 1 && n <= max && n <= MAX_SAFE;
}

// ---------------------------------------------------------------------------
// Babylonian

const CUNEIFORM_ONE = "𒁹";
const CUNEIFORM_TEN = "𒌋";
const EM_SPACE = " ";

export type BabylonianZero = "gap" | "placeholder";

export interface BabylonianResult {
  /** Base-60 digits, most significant first (an empty place is 0). */
  digits: number[];
  /** Cuneiform text with the chosen empty-place style; trailing zeros are not written. */
  text: string;
  /** The same in typed form: Y for one and < for ten. */
  plain: string;
  /** How many trailing empty places were left unwritten. */
  droppedZeros: number;
}

/** A number from 1 to 999 999 999 999 in base 60, most significant digit first. */
export function toBase60(n: number): number[] {
  const digits: number[] = [];
  let rest = n;
  while (rest > 0) {
    digits.unshift(rest % 60);
    rest = Math.floor(rest / 60);
  }
  return digits;
}

function babylonianDigit(d: number): { glyph: string; plain: string } {
  const tens = Math.floor(d / 10);
  const ones = d % 10;
  return {
    glyph: CUNEIFORM_TEN.repeat(tens) + CUNEIFORM_ONE.repeat(ones),
    plain: "<".repeat(tens) + "Y".repeat(ones),
  };
}

export function babylonian(
  n: number,
  zero: BabylonianZero
): BabylonianResult | null {
  if (!isCount(n, BABYLONIAN_MAX)) {
    return null;
  }
  const digits = toBase60(n);
  let end = digits.length;
  while (end > 1 && digits[end - 1] === 0) {
    end--;
  }
  const written = digits.slice(0, end);
  const glyphs: string[] = [];
  const plains: string[] = [];
  for (const d of written) {
    if (d === 0) {
      glyphs.push(zero === "gap" ? EM_SPACE : "∅");
      plains.push(zero === "gap" ? "  " : "∅");
    } else {
      const { glyph, plain } = babylonianDigit(d);
      glyphs.push(glyph);
      plains.push(plain);
    }
  }
  return {
    digits,
    text: glyphs.join(" "),
    plain: plains.join(" "),
    droppedZeros: digits.length - end,
  };
}

// ---------------------------------------------------------------------------
// Egyptian

export interface EgyptianSign {
  value: number;
  glyph: string;
  /** Gardiner sign-list code. */
  code: string;
}

export const EGYPTIAN_SIGNS: readonly EgyptianSign[] = [
  { value: 1_000_000, glyph: "\u{13068}", code: "C11" },
  { value: 100_000, glyph: "\u{13190}", code: "I8" },
  { value: 10_000, glyph: "\u{130AD}", code: "D50" },
  { value: 1000, glyph: "\u{131BC}", code: "M12" },
  { value: 100, glyph: "\u{13362}", code: "V1" },
  { value: 10, glyph: "\u{13386}", code: "V20" },
  { value: 1, glyph: "\u{133E4}", code: "Z1" },
];

export interface EgyptianRow {
  sign: EgyptianSign;
  count: number;
}

export interface EgyptianResult {
  rows: EgyptianRow[];
  text: string;
}

export function egyptian(n: number): EgyptianResult | null {
  if (!isCount(n, EGYPTIAN_MAX)) {
    return null;
  }
  let rest = n;
  const rows: EgyptianRow[] = [];
  for (const sign of EGYPTIAN_SIGNS) {
    const count = Math.floor(rest / sign.value);
    rest -= count * sign.value;
    if (count > 0) {
      rows.push({ sign, count });
    }
  }
  return {
    rows,
    text: rows.map((r) => r.sign.glyph.repeat(r.count)).join(" "),
  };
}

// ---------------------------------------------------------------------------
// Maya

const MAYA_ZERO = 0x1_d2_e0;

export type MayaMode = "base20" | "longCount";

export interface MayaPlace {
  /** Name of the place: "Winal" in Long Count mode, "20^1" otherwise. */
  name: string;
  /** What one unit of this place is worth. */
  value: number;
  digit: number;
  glyph: string;
}

export interface MayaResult {
  /** Places from the highest down to the units. */
  places: MayaPlace[];
  /** The digits joined with dots: 13.0.0.0.0 */
  dotted: string;
}

const LONG_COUNT_NAMES = [
  "K'in",
  "Winal",
  "Tun",
  "K'atun",
  "B'ak'tun",
  "Piktun",
  "Kalabtun",
  "K'inchiltun",
  "Alautun",
] as const;

/** Positional bases from the units up: 20 everywhere, except 18 for the winal in Long Count. */
function mayaBase(index: number, mode: MayaMode): number {
  return mode === "longCount" && index === 1 ? 18 : 20;
}

export function maya(n: number, mode: MayaMode): MayaResult | null {
  if (!isCount(n, MAYA_MAX)) {
    return null;
  }
  const places: MayaPlace[] = [];
  let rest = n;
  let value = 1;
  for (let index = 0; rest > 0; index++) {
    const base = mayaBase(index, mode);
    const digit = rest % base;
    places.unshift({
      name: mode === "longCount" ? LONG_COUNT_NAMES[index] : `20^${index}`,
      value,
      digit,
      glyph: String.fromCodePoint(MAYA_ZERO + digit),
    });
    rest = Math.floor(rest / base);
    value *= base;
  }
  return { places, dotted: places.map((p) => p.digit).join(".") };
}

// ---------------------------------------------------------------------------
// Attic Greek

export interface AtticSign {
  value: number;
  glyph: string;
  /** Plain-letter form for fonts without the Ancient Greek Numbers block. */
  fallback: string;
}

export const ATTIC_SIGNS: readonly AtticSign[] = [
  { value: 50_000, glyph: "\u{10147}", fallback: "[ΠΜ]" },
  { value: 10_000, glyph: "Μ", fallback: "Μ" },
  { value: 5000, glyph: "\u{10146}", fallback: "[ΠΧ]" },
  { value: 1000, glyph: "Χ", fallback: "Χ" },
  { value: 500, glyph: "\u{10145}", fallback: "[ΠΗ]" },
  { value: 100, glyph: "Η", fallback: "Η" },
  { value: 50, glyph: "\u{10144}", fallback: "[ΠΔ]" },
  { value: 10, glyph: "Δ", fallback: "Δ" },
  { value: 5, glyph: "Π", fallback: "Π" },
  { value: 1, glyph: "Ι", fallback: "Ι" },
];

export interface AtticRow {
  sign: AtticSign;
  count: number;
}

export interface AtticResult {
  rows: AtticRow[];
  text: string;
  fallback: string;
}

export function attic(n: number): AtticResult | null {
  if (!isCount(n, ATTIC_MAX)) {
    return null;
  }
  let rest = n;
  const rows: AtticRow[] = [];
  for (const sign of ATTIC_SIGNS) {
    const count = Math.floor(rest / sign.value);
    rest -= count * sign.value;
    if (count > 0) {
      rows.push({ sign, count });
    }
  }
  return {
    rows,
    text: rows.map((r) => r.sign.glyph.repeat(r.count)).join(""),
    fallback: rows.map((r) => r.sign.fallback.repeat(r.count)).join(""),
  };
}

// ---------------------------------------------------------------------------
// Ionic (Milesian) Greek

const KERAIA = "ʹ";
const LOWER_KERAIA = "͵";

const IONIC_UNITS = ["", "α", "β", "γ", "δ", "ε", "ϛ", "ζ", "η", "θ"] as const;
const IONIC_TENS = ["", "ι", "κ", "λ", "μ", "ν", "ξ", "ο", "π", "ϟ"] as const;
const IONIC_HUNDREDS = [
  "",
  "ρ",
  "σ",
  "τ",
  "υ",
  "φ",
  "χ",
  "ψ",
  "ω",
  "ϡ",
] as const;

export interface IonicLetter {
  letter: string;
  value: number;
}

export interface IonicResult {
  letters: IonicLetter[];
  text: string;
}

export const IONIC_LETTERS: readonly IonicLetter[] = [
  ...IONIC_HUNDREDS.map((letter, i) => ({ letter, value: i * 100 })),
  ...IONIC_TENS.map((letter, i) => ({ letter, value: i * 10 })),
  ...IONIC_UNITS.map((letter, i) => ({ letter, value: i })),
].filter((l) => l.value > 0);

export function ionic(n: number): IonicResult | null {
  if (!isCount(n, IONIC_MAX)) {
    return null;
  }
  const thousands = Math.floor(n / 1000);
  const hundreds = Math.floor(n / 100) % 10;
  const tens = Math.floor(n / 10) % 10;
  const units = n % 10;
  const letters: IonicLetter[] = [];
  if (thousands > 0) {
    letters.push({
      letter: LOWER_KERAIA + IONIC_UNITS[thousands],
      value: thousands * 1000,
    });
  }
  const parts: [readonly string[], number, number][] = [
    [IONIC_HUNDREDS, hundreds, 100],
    [IONIC_TENS, tens, 10],
    [IONIC_UNITS, units, 1],
  ];
  for (const [table, digit, scale] of parts) {
    if (digit > 0) {
      letters.push({ letter: table[digit], value: digit * scale });
    }
  }
  return {
    letters,
    text: letters.map((l) => l.letter).join("") + KERAIA,
  };
}
